// src/components/notifications/SubscribePushButton.jsx
// Generic browser-push subscribe button. Render anywhere a user might want to
// opt into FCM notifications (offer received, future: truck departure, chat).
// Caller passes labels + helper copy; the hook drives state, this is presentation.

import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useFcmSubscription } from "src/hooks/useFcmSubscription";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import { useT } from "src/i18n";

export default function SubscribePushButton({
    promptLabel,
    successLabel,
    helperText = null,
    sx,
}) {
    const t = useT();
    const { status, lastError, subscribe } = useFcmSubscription();
    const resolvedPromptLabel = promptLabel ?? t("common.subscribePush.promptLabel");
    const resolvedSuccessLabel = successLabel ?? t("common.subscribePush.successLabel");

    if (status === "checking" || status === "unsupported") {
        // Stay invisible while detecting or on browsers without push support.
        // No value in surfacing "your browser doesn't support this" for v1.
        return null;
    }

    if (status === "ios-needs-pwa") {
        return (
            <Stack spacing={0.5} sx={{ alignItems: "center", ...sx }}>
                <Typography variant="caption" align="center" sx={{ color: "text.secondary" }}>
                    {t("common.subscribePush.iosNeedsPwaHint")}
                </Typography>
            </Stack>
        );
    }

    if (status === "granted") {
        return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", justifyContent: "center", color: "success.main", ...sx }}>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="body2">{resolvedSuccessLabel}</Typography>
            </Stack>
        );
    }

    if (status === "denied") {
        return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", justifyContent: "center", color: "text.secondary", ...sx }}>
                <NotificationsOffIcon fontSize="small" />
                <Typography variant="caption" align="center">
                    {t("common.subscribePush.deniedHint")}
                </Typography>
            </Stack>
        );
    }

    const isLoading = status === "registering";

    return (
        <Stack spacing={1} sx={{ alignItems: "center", ...sx }}>
            <Button
                variant="contained"
                onClick={subscribe}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <NotificationsActiveIcon />}
                sx={[amberCtaSx, { py: 1.25, px: 3 }]}
            >
                {isLoading ? t("common.subscribePush.subscribing") : resolvedPromptLabel}
            </Button>
            {helperText && (
                <Typography variant="caption" align="center" sx={{ color: "text.secondary" }}>
                    {helperText}
                </Typography>
            )}
            {status === "error" && lastError && (
                <Box sx={{ color: "error.main", typography: "caption", textAlign: "center" }}>{lastError}</Box>
            )}
        </Stack>
    );
}
